import axios from 'axios'
import { AuthService } from '../services/authService'
import { API_BASE_URL } from '../config/api'

// CSRF token cache.
//
// In production the React app and the API are on different origins, so
// document.cookie on the frontend host cannot read the csrfToken cookie that
// the API sets on its own host. We keep the token in memory instead, seeding
// it from the cookie when same-origin makes that possible (dev) and otherwise
// fetching it from GET /auth/csrf-token. The fetch is deduped via
// `csrfTokenPromise` so a burst of mutations triggers at most one round trip.
let cachedCsrfToken = null
let csrfTokenPromise = null

const readCsrfCookie = () => {
  const match = document.cookie.match(/(?:^|;\s*)csrfToken=([^;]*)/)
  return match ? decodeURIComponent(match[1]) : null
}

const fetchCsrfToken = () => {
  if (csrfTokenPromise) return csrfTokenPromise
  // Use a bare axios.get so this request still hits our request interceptor
  // (which sets baseURL + withCredentials), but the interceptor's CSRF block
  // skips GET — no recursion.
  csrfTokenPromise = axios
    .get('/auth/csrf-token')
    .then((res) => {
      cachedCsrfToken = res.data?.csrfToken || null
      return cachedCsrfToken
    })
    .catch((err) => {
      // Don't poison the cache on failure — let the next mutation try again.
      cachedCsrfToken = null
      throw err
    })
    .finally(() => {
      csrfTokenPromise = null
    })
  return csrfTokenPromise
}

const getCsrfToken = async () => {
  if (cachedCsrfToken) return cachedCsrfToken
  const fromCookie = readCsrfCookie()
  if (fromCookie) {
    cachedCsrfToken = fromCookie
    return cachedCsrfToken
  }
  return fetchCsrfToken()
}

export const setupAxiosInterceptors = (dispatch, AUTH_ACTIONS) => {
  // Request interceptor
  const requestInterceptor = axios.interceptors.request.use(
    async (config) => {
      // Add base URL if not set
      if (!config.url.startsWith('http')) {
        config.baseURL = API_BASE_URL
      }

      // Add credentials for cookie-based auth
      config.withCredentials = true

      // Attach CSRF token for state-changing requests
      if (!['get', 'head', 'options'].includes((config.method || '').toLowerCase())) {
        try {
          const token = await getCsrfToken()
          if (token) {
            config.headers['X-CSRF-Token'] = token
          }
        } catch (err) {
          if (process.env.NODE_ENV === 'development') console.error('CSRF token fetch failed:', err)
          // Fall through without the header — the server will 403, the
          // response interceptor below will clear the cache and retry once.
        }
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  // Response interceptor
  const responseInterceptor = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config

      // Handle token expiration
      if (error.response?.status === 401 &&
        error.response?.data?.code === 'TOKEN_EXPIRED' &&
        !originalRequest._retry) {

        originalRequest._retry = true

        try {
          await AuthService.refreshToken()
          return axios(originalRequest)
        } catch (refreshError) {
          if (process.env.NODE_ENV === 'development') console.error('Token refresh failed:', refreshError)
          dispatch({ type: AUTH_ACTIONS.SET_UNAUTHENTICATED })
          return Promise.reject(refreshError)
        }
      }

      // Handle CSRF failure: token rotated or cache went stale. Invalidate
      // and retry once with a fresh token.
      const csrfCode = error.response?.data?.code
      if (error.response?.status === 403 &&
        (csrfCode === 'CSRF_MISSING' || csrfCode === 'CSRF_INVALID') &&
        originalRequest && !originalRequest._csrfRetry) {

        originalRequest._csrfRetry = true
        cachedCsrfToken = null
        try {
          await fetchCsrfToken()
          return axios(originalRequest)
        } catch (csrfErr) {
          return Promise.reject(csrfErr)
        }
      }

      return Promise.reject(error)
    }
  )

  // Return cleanup function
  return () => {
    axios.interceptors.request.eject(requestInterceptor)
    axios.interceptors.response.eject(responseInterceptor)
  }
}
